import * as React from 'react';
import { useUser } from 'lib/auth';
import { Link } from 'components/ui/link';
import {
    MEDICARD_EXTERNAL_ORGANIZATION_ID,
    SALA_AIA_ORGANIZATION_ID,
    ATOMIX_ORGANIZATION_ID,
    ATOMIX_NO_PHI_ORGANIZATION_ID,
    RPM_ORGANIZATION_ID,
    ASTER_ORGANIZATION_ID,
    SALA_MAXICARE_ORGANIZATION_ID,
    PACIFIC_CROSS_ORGANIZATION_ID,
} from './constants';
import {
    Bot,
    Cog,
    Database,
    Frame,
    Rocket,
    Settings2,
    TerminalSquare,
    Scale,
    Hammer,
    ReceiptText,
    Key,
    Telescope,
    BookOpen,
    Lock,
    ScanText,
    File,
    FileCheck,
    FileText,
    ChartLine,
    ChartColumn,
    Tags,
    Pill,
    ClipboardPlus,
    ClipboardCheck,
    ListChecks,
    ShieldAlert,
    Search,
    Settings,
    Brain,
} from 'lucide-react';
import logo from 'assets/logo.svg';
import logoDark from 'assets/logo-dark.svg';

import { NavMain } from 'components/layouts/dashboard-layout/nav-main';
import { NavProjects } from 'components/layouts/dashboard-layout/nav-projects';
import { NavSecondary } from 'components/layouts/dashboard-layout/nav-secondary';
import { NavUser } from 'components/layouts/dashboard-layout/nav-user';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from 'components/ui/sidebar';
import { User } from 'types/api';
import { useTheme } from 'components/styles/theme-provider';

const EXCLUDED_ORGANIZATIONS = [
    MEDICARD_EXTERNAL_ORGANIZATION_ID,
    ASTER_ORGANIZATION_ID,
    RPM_ORGANIZATION_ID,
    SALA_MAXICARE_ORGANIZATION_ID,
    SALA_AIA_ORGANIZATION_ID,
    PACIFIC_CROSS_ORGANIZATION_ID,
];

const data = {
    navMain: [
        {
            title: 'Projects',
            url: '/app/projects',
            icon: Bot,
            isActive: true,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Datasets',
            url: '/app/datasets',
            icon: Database,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Graders',
            url: '/app/graders',
            icon: Scale,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Evaluations',
            url: '/app/evaluations',
            icon: ClipboardCheck,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Tools',
            url: '/app/tools',
            icon: Hammer,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Deployments',
            url: '/app/deployments',
            icon: Rocket,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Telemetry',
            url: '/app/telemetry',
            icon: Telescope,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Claims Lookup',
            url: '/app/medicard-claims',
            icon: File,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'Insurance Analytics',
            url: '/app/insurance-analytics',
            icon: ChartLine,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    ATOMIX_NO_PHI_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        // {
        //     title: 'Maxicare Analytics Chat',
        //     url: '/app/maxicare-analytics-chat',
        //     icon: MessageCircle,
        //     condition: (user: User | undefined) =>
        //         user?.organization.id ===
        //         SALA_AIA_ORGANIZATION_ID,
        // },
        {
            title: 'Medication Analytics',
            url: '/app/medication-analytics',
            icon: Pill,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    MEDICARD_EXTERNAL_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Medicard POC grading',
            url: '/app/medicard-poc-grading',
            icon: ListChecks,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    MEDICARD_EXTERNAL_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Medical Code Lookup',
            url: '/app/medical-code-lookup',
            icon: Search,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'CPT Code Configuration',
            url: '/app/maxicare-cpt-mapping',
            icon: Settings2,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_MAXICARE_ORGANIZATION_ID,
        },
        {
            title: 'Transcript Extraction',
            url: '/app/transcript-demo',
            icon: FileText,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'Claims AI',
            url: '/app/insurance-extraction/tasks',
            icon: ClipboardPlus,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    SALA_MAXICARE_ORGANIZATION_ID,
                    // MEDICARD_EXTERNAL_ORGANIZATION_ID,
                    PACIFIC_CROSS_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Field Configuration',
            url: '/app/insurance-extraction-config',
            icon: Settings,
            condition: (user: User | undefined) =>
                [
                    SALA_MAXICARE_ORGANIZATION_ID,
                    PACIFIC_CROSS_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Claim Analytics',
            url: '/app/claim-analytics',
            icon: ChartLine,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    SALA_MAXICARE_ORGANIZATION_ID,
                    // MEDICARD_EXTERNAL_ORGANIZATION_ID,
                    PACIFIC_CROSS_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Pacific Cross Demo',
            url: '/app/pacific-cross-demo',
            icon: File,
            condition: (user: User | undefined) =>
                [PACIFIC_CROSS_ORGANIZATION_ID].includes(
                    user?.organization.id || '',
                ),
        },
        {
            title: 'FWA Detection',
            url: '/app/fwa-dashboard',
            icon: ShieldAlert,
            condition: (user: User | undefined) =>
                [
                    SALA_MAXICARE_ORGANIZATION_ID,
                    SALA_AIA_ORGANIZATION_ID,
                    PACIFIC_CROSS_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Entity Extraction',
            url: '/app/entity-extraction',
            icon: Tags,
            condition: (user: User | undefined) =>
                [
                    ATOMIX_ORGANIZATION_ID,
                    ATOMIX_NO_PHI_ORGANIZATION_ID,
                ].includes(user?.organization?.id || ''),
        },
        {
            title: 'RPM Invoice Extraction',
            url: '/app/rpm',
            icon: ReceiptText,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Payroll Vendors',
            url: '/app/rpm/vendors',
            icon: Settings2,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Extraction Rules',
            url: '/app/rpm/rules',
            icon: Scale,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Vendor Analytics',
            url: '/app/rpm/vendor-analytics',
            icon: ChartColumn,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        // {
        //     title: 'Aster Analytics Demo',
        //     url: '/app/aster-analytics-demo',
        //     icon: BarChart4,
        //     condition: (user: User | undefined) =>
        //         [
        //             ATOMIX_ORGANIZATION_ID,
        //             ATOMIX_NO_PHI_ORGANIZATION_ID,
        //         ].includes(user?.organization?.id || ''),
        // },
        {
            title: 'Invoice Extraction',
            url: '/app/aster-invoice-extraction',
            icon: File,
            condition: (user: User | undefined) =>
                [ASTER_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'API Keys',
            url: '/app/api-keys',
            icon: Key,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Secrets',
            url: '/app/secrets',
            icon: Lock,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'OCR',
            url: '/app/ocr',
            icon: ScanText,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Documentation',
            icon: BookOpen,
            url: '/app/documentation',
            items: [
                {
                    title: 'Introduction',
                    url: '/app/documentation/introduction',
                },
                {
                    title: 'Projects Guide',
                    url: '/app/documentation/projects',
                },
                {
                    title: 'Datasets Guide',
                    url: '/app/documentation/datasets',
                },
                {
                    title: 'Graders Guide',
                    url: '/app/documentation/graders',
                },
                {
                    title: 'Tools Guide',
                    url: '/app/documentation/tools',
                },
                {
                    title: 'Implementations Guide',
                    url: '/app/documentation/implementations',
                },
                {
                    title: 'Deployments Guide',
                    url: '/app/documentation/deployments',
                },
                {
                    title: 'Telemetry Guide',
                    url: '/app/documentation/telemetry',
                },
                {
                    title: 'Secrets Guide',
                    url: '/app/documentation/secrets',
                },
                {
                    title: 'OCR Guide',
                    url: '/app/documentation/ocr',
                },
                {
                    title: 'Data Quality Review Guide',
                    url: '/app/documentation/data-quality-review',
                },
                {
                    title: 'API Documentation',
                    url: '/app/documentation/api',
                },
                {
                    title: 'Model Pricing',
                    url: '/app/documentation/model-pricing',
                },
            ],
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'AI Analytics',
            url: '/app/analytics-ai',
            icon: Brain,
            condition: (user: User | undefined) =>
                [PACIFIC_CROSS_ORGANIZATION_ID].includes(
                    user?.organization.id || '',
                ),
        },
    ],
    navSecondary: [
        {
            title: 'Settings',
            url: '/app/settings',
            icon: Cog,
        },
        // {
        //     title: 'Support',
        //     url: '/app#',
        //     icon: LifeBuoy,
        // },
        // {
        //     title: 'Feedback',
        //     url: '/app#',
        //     icon: Send,
        // },
    ],
    projects: [
        {
            name: 'Overview',
            url: '/app/projects/:projectId',
            icon: Frame,
        },
        {
            name: 'Configuration',
            url: '/app/projects/:projectId/configuration',
            icon: Settings2,
        },
        {
            name: 'Data Quality Review',
            url: '/app/projects/:projectId/data-quality-review',
            icon: FileCheck,
        },
        {
            name: 'Implementations',
            url: '/app/projects/:projectId/implementations',
            icon: TerminalSquare,
        },
    ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { data: user } = useUser();
    const { isDark } = useTheme();
    return (
        <Sidebar variant="inset" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" asChild>
                            <Link to="/app">
                                <div className="flex aspect-square items-center justify-center text-sidebar-primary-foreground">
                                    <img
                                        src={isDark ? logoDark : logo}
                                        alt="Atomix"
                                        className="h-8 w-8"
                                    />
                                </div>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">
                                        Atomix
                                    </span>
                                </div>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                <NavMain items={data.navMain} user={user} />
                <NavProjects projects={data.projects} />
                <NavSecondary items={data.navSecondary} className="mt-auto" />
            </SidebarContent>
            <SidebarFooter>
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
